import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Paper,
  TextField,
  Button,
  Snackbar,
  Alert,
  useTheme,
  AppBar,
  Toolbar,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  ListItemIcon,
  InputAdornment,
  Menu,
  MenuItem,
} from '@mui/material';
import {
  Email as EmailIcon,
  Phone as PhoneIcon,
  LocationOn as LocationIcon,
  Instagram as InstagramIcon,
  Language as WebsiteIcon,
  Menu as MenuIcon,
} from '@mui/icons-material';
import { useHistory } from 'react-router-dom';
import PublicLayout from '../components/PublicLayout';
import { Link } from 'react-router-dom';
import PublicFooter from '../components/layout/PublicFooter';
import { useForm, ValidationError } from '@formspree/react';

const Contact = () => {
  const theme = useTheme();
  const history = useHistory();
  const [mobileMenuAnchor, setMobileMenuAnchor] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: '',
  });
  const [state, handleSubmit] = useForm("xyzedggd");

  const handleMobileMenuOpen = (event) => {
    setMobileMenuAnchor(event.currentTarget);
  };

  const handleMobileMenuClose = () => {
    setMobileMenuAnchor(null);
  };

  const handleNavigation = (path) => {
    history.push(path);
    handleMobileMenuClose();
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    await handleSubmit(e);
    if (state.succeeded) {
      setFormData({
        name: '',
        email: '',
        subject: '',
        message: '',
      });
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const contactInfo = [
    {
      icon: <EmailIcon sx={{ fontSize: 40, color: '#ff9800' }} />,
      title: 'Email',
      content: 'info@vendoroll.com',
      link: 'mailto:info@vendoroll.com',
    },
    {
      icon: <PhoneIcon sx={{ fontSize: 40, color: '#ff9800' }} />,
      title: 'Phone',
      content: '(587) 574-8457',
      link: 'tel:+15875748457',
    },
    {
      icon: <LocationIcon sx={{ fontSize: 40, color: '#ff9800' }} />,
      title: 'Location',
      content: 'Calgary, Alberta, Canada',
      link: 'https://maps.google.com',
    },
  ];

  return (
    <PublicLayout>
      {/* Minimal Header */}
      <AppBar 
        position="static" 
        sx={{ 
          bgcolor: 'white',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        }}
      >
        <Toolbar>
          <Typography
            variant="h6"
            component="div"
            sx={{
              flexGrow: 1,
              fontWeight: 600,
              color: theme.palette.primary.main,
              cursor: 'pointer',
            }}
            onClick={() => history.push('/')}
          >
            Vendoroll
          </Typography>
          
          {/* Desktop Navigation */}
          <Box sx={{ display: { xs: 'none', md: 'flex' }, gap: 2, alignItems: 'center' }}>
            <Button
              color="primary"
              onClick={() => history.push('/')}
              sx={{
                color: '#ff9800',
                '&:hover': {
                  bgcolor: 'rgba(255, 152, 0, 0.04)',
                },
              }}
            >
              Home
            </Button>
            <Button
              color="primary"
              onClick={() => history.push('/about')}
              sx={{
                color: '#ff9800',
                '&:hover': {
                  bgcolor: 'rgba(255, 152, 0, 0.04)',
                },
              }}
            >
              About Us
            </Button>
            <Button
              color="primary"
              onClick={() => history.push('/contact')}
              sx={{
                color: '#ff9800',
                '&:hover': {
                  bgcolor: 'rgba(255, 152, 0, 0.04)',
                },
              }}
            >
              Contact
            </Button>
            <Button
              variant="contained"
              onClick={() => history.push('/#waitlist')}
              sx={{
                bgcolor: '#ff9800',
                '&:hover': {
                  bgcolor: '#e68a00',
                },
                borderRadius: 20,
                px: 3,
              }}
            >
              Join Waitlist
            </Button>
          </Box>
          
          {/* Mobile Navigation */}
          <Box sx={{ display: { xs: 'flex', md: 'none' } }}>
            <Button
              variant="contained"
              onClick={() => history.push('/#waitlist')}
              sx={{
                bgcolor: '#ff9800',
                '&:hover': {
                  bgcolor: '#e68a00',
                },
                borderRadius: 20,
                px: 3,
                mr: 1,
              }}
            >
              Join Waitlist
            </Button>
            <IconButton
              color="primary"
              onClick={handleMobileMenuOpen}
              sx={{ color: '#ff9800' }}
            >
              <MenuIcon />
            </IconButton>
            <Menu
              anchorEl={mobileMenuAnchor}
              open={Boolean(mobileMenuAnchor)}
              onClose={handleMobileMenuClose}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'right',
              }}
              transformOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
            >
              <MenuItem onClick={() => handleNavigation('/')}>Home</MenuItem>
              <MenuItem onClick={() => handleNavigation('/about')}>About Us</MenuItem>
              <MenuItem onClick={() => handleNavigation('/contact')}>Contact</MenuItem>
            </Menu>
          </Box>
        </Toolbar>
      </AppBar>

      {/* Hero Section */}
      <Box
        sx={{
          background: `linear-gradient(45deg, #ff9800 30%, #f57c00 90%)`,
          color: 'white',
          py: { xs: 6, md: 8 },
          mb: 6,
        }}
      >
        <Container maxWidth="lg">
          <Typography
            variant="h2"
            component="h1"
            gutterBottom
            sx={{
              fontWeight: 700,
              mb: 2,
            }}
          >
            Contact Us
          </Typography>
          <Typography variant="h5" sx={{ maxWidth: '800px' }}>
            Have questions? We'd love to hear from you. Send us a message and we'll respond as soon as possible.
          </Typography>
        </Container>
      </Box>

      {/* Contact Information */}
      <Container maxWidth="lg" sx={{ mb: 8 }}>
        <Grid container spacing={4}>
          {contactInfo.map((info, index) => (
            <Grid item xs={12} sm={4} key={index}>
              <Paper
                elevation={2}
                sx={{
                  p: 3,
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  textAlign: 'center',
                  transition: 'transform 0.2s',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                  },
                }}
              >
                <Box sx={{ mb: 2 }}>{info.icon}</Box>
                <Typography variant="h6" gutterBottom>
                  {info.title}
                </Typography>
                <Button
                  href={info.link}
                  sx={{
                    color: '#ff9800',
                    '&:hover': {
                      color: '#f57c00',
                    },
                  }}
                >
                  {info.content}
                </Button>
              </Paper>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* Contact Form */}
      <Box sx={{ bgcolor: 'grey.50', py: { xs: 6, md: 8 } }}>
        <Container maxWidth="md">
          <Paper
            elevation={3}
            sx={{
              p: 4,
              bgcolor: 'white',
              borderRadius: 2,
            }}
          >
            <Typography
              variant="h4"
              component="h2"
              gutterBottom
              sx={{
                color: '#ff9800',
                fontWeight: 700,
                mb: 4,
              }}
            >
              Send us a Message
            </Typography>
            <form onSubmit={handleFormSubmit}>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    variant="outlined"
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    variant="outlined"
                  />
                  <ValidationError 
                    prefix="Email" 
                    field="email"
                    errors={state.errors}
                    sx={{ color: 'error.main', mt: 1 }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Subject"
                    name="subject"
                    value={formData.subject}
                    onChange={handleChange}
                    required
                    variant="outlined"
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Message"
                    name="message"
                    multiline
                    rows={4}
                    value={formData.message}
                    onChange={handleChange}
                    required
                    variant="outlined"
                  />
                  <ValidationError 
                    prefix="Message" 
                    field="message"
                    errors={state.errors}
                    sx={{ color: 'error.main', mt: 1 }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Button
                    type="submit"
                    variant="contained"
                    size="large"
                    fullWidth
                    disabled={state.submitting}
                    sx={{
                      bgcolor: '#ff9800',
                      '&:hover': {
                        bgcolor: '#f57c00',
                      },
                    }}
                  >
                    {state.submitting ? 'Sending...' : 'Send Message'}
                  </Button>
                </Grid>
              </Grid>
            </form>
            {state.succeeded && (
              <Alert 
                severity="success" 
                sx={{ mt: 2 }}
              >
                Thank you for your message! We'll get back to you soon.
              </Alert>
            )}
          </Paper>
        </Container>
      </Box>

      {/* Footer */}
      <PublicFooter />
    </PublicLayout>
  );
};

export default Contact; 